import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'

import { MaxLoanToPropertyValue, MinPrincipalAmount } from '../../../utils/Constants'
import {
	deleteCalculatorDataFromLocalStorage,
	getCalculatorDataFromLocalStorage,
} from '../../../utils/browserStorage/LocalStorage'
import { setCalculatorDataToSessionStorage } from '../../../utils/browserStorage/SessionStorage'
import {
	GetMortgageSettingsResponse,
	initialLoanCalculationValuesTypeA,
	initialLoanCalculationValuesTypeB,
	LoanCalculationCalculatedData,
	LoanCalculationInputs,
	LoanTypeEnum,
	LoanTypeEnumCamelCase,
} from '../newCalculator.models'

import useCalculatorRequests from './useCalculatorRequests'

// /mortgages/settings can fail (e.g. BE 500). Retry indefinitely with
// exponential backoff (double the wait each time) so a failing endpoint never
// causes a tight refetch loop. The delay is capped because setTimeout stores
// its delay as a 32-bit signed int — beyond ~24.8 days it overflows and fires
// immediately, which would defeat the backoff.
const SETTINGS_INITIAL_RETRY_DELAY_MS = 3000
const SETTINGS_MAX_RETRY_DELAY_MS = 5 * 60 * 1000

export type LocalCalculationValues = {
	ASSET_FORMATION: LoanCalculationInputs
	PAYMENT_REDUCTION: LoanCalculationInputs
}

export type LocalCalculatedData = {
	ASSET_FORMATION: LoanCalculationCalculatedData
	PAYMENT_REDUCTION: LoanCalculationCalculatedData
}

const getInitialCalculatorValues = (loanType: LoanTypeEnum) => {
	let storageCalculatorData = getCalculatorDataFromLocalStorage()

	if (
		storageCalculatorData &&
		(!isValidLoanCalculationInputs(storageCalculatorData.ASSET_FORMATION) ||
			!isValidLoanCalculationInputs(storageCalculatorData.PAYMENT_REDUCTION))
	) {
		deleteCalculatorDataFromLocalStorage()
		storageCalculatorData = undefined
	}

	if (loanType === LoanTypeEnum.ASSET_FORMATION) {
		return storageCalculatorData?.ASSET_FORMATION ?? initialLoanCalculationValuesTypeA
	}

	return storageCalculatorData?.PAYMENT_REDUCTION ?? initialLoanCalculationValuesTypeB
}

function isValidLoanCalculationInputs(obj: any): obj is LoanCalculationInputs {
	const expectedKeys: (keyof LoanCalculationInputs)[] = [
		'loanAmount',
		'propertyMarketValueAmount',
		'annualHousingPriceIncreasePercentage',
		'optionBAmount',
		'optionBLengthInYears',
		'estimatedPropertyAnnual10YearOption',
		'numberOfPaymentsMonths',
		'fixedInterestPercentage',
		'extraMonthlyPaymentToPrincipal',
		// optional ones (validate if present)
		'principalPaymentType',
		'firstPaymentDate',
		'selectedPlan',
	]

	if (typeof obj !== 'object' || obj === null) return false

	const objKeys = Object.keys(obj)
	const requiredKeys = expectedKeys.slice(0, 9) // first 9 are required

	// Check for missing required keys
	const missingKeys = requiredKeys.filter((key) => !(key in obj))
	if (missingKeys.length > 0) return false

	// Check for unknown keys
	const unknownKeys = objKeys.filter((key) => !expectedKeys.includes(key as keyof LoanCalculationInputs))
	if (unknownKeys.length > 0) return false

	// Type checks
	return (
		typeof obj.loanAmount === 'number' &&
		typeof obj.propertyMarketValueAmount === 'number' &&
		typeof obj.annualHousingPriceIncreasePercentage === 'number' &&
		typeof obj.optionBAmount === 'number' &&
		typeof obj.optionBLengthInYears === 'number' &&
		typeof obj.estimatedPropertyAnnual10YearOption === 'boolean' &&
		typeof obj.numberOfPaymentsMonths === 'number' &&
		typeof obj.fixedInterestPercentage === 'number' &&
		typeof obj.extraMonthlyPaymentToPrincipal === 'number' &&
		(obj.principalPaymentType === undefined || typeof obj.principalPaymentType === 'string') &&
		(obj.firstPaymentDate === undefined || typeof obj.firstPaymentDate === 'string') &&
		(obj.selectedPlan === undefined || typeof obj.selectedPlan === 'string')
	)
}

export const useCalculatorLogic = () => {
	const { calculateLoanData, getCalculatorOptions } = useCalculatorRequests()

	const [loanCalculationValues, setLoanCalculationValues] = useState<LocalCalculationValues>({
		ASSET_FORMATION: getInitialCalculatorValues(LoanTypeEnum.ASSET_FORMATION),
		PAYMENT_REDUCTION: getInitialCalculatorValues(LoanTypeEnum.PAYMENT_REDUCTION),
	})

	const [expandedPaymentPlan, setExpandedPaymentPlan] = useState<LoanTypeEnum | undefined>()
	const [calculatedData, setCalculatedData] = useState<LocalCalculatedData>()
	const [defaultOptions, setDefaultOptions] = useState<GetMortgageSettingsResponse | null>(null)
	const [publicShowSettings, setPublicShowSettings] = useState<boolean>(false)

	const onInputValueChange = (
		value: number | boolean | string | undefined,
		propertyName: keyof LoanCalculationInputs,
		optionA: boolean,
		optionB: boolean
	) => {
		let ASSET_FORMATION = { ...loanCalculationValues.ASSET_FORMATION }
		if (optionA) {
			ASSET_FORMATION = {
				...ASSET_FORMATION,
				[propertyName]: value,
			}
		}

		let PAYMENT_REDUCTION = { ...loanCalculationValues.PAYMENT_REDUCTION }
		if (optionB) {
			PAYMENT_REDUCTION = {
				...PAYMENT_REDUCTION,
				[propertyName]: value,
			}
		}

		// Track which type of field was last changed so the request layer knows
		// whether to use the user's typed rate or the LTV-derived rate.
		if (propertyName === 'fixedInterestPercentage') {
			if (optionA) ASSET_FORMATION = { ...ASSET_FORMATION, useCustomInterestRate: true }
			if (optionB) PAYMENT_REDUCTION = { ...PAYMENT_REDUCTION, useCustomInterestRate: true }
		} else if (propertyName === 'loanAmount' || propertyName === 'propertyMarketValueAmount') {
			if (optionA) ASSET_FORMATION = { ...ASSET_FORMATION, useCustomInterestRate: false }
			if (optionB) PAYMENT_REDUCTION = { ...PAYMENT_REDUCTION, useCustomInterestRate: false }
		}

		setLoanCalculationValues({ ASSET_FORMATION, PAYMENT_REDUCTION })
		setCalculatorDataToSessionStorage(ASSET_FORMATION, PAYMENT_REDUCTION)
	}

	const onMultipleInputValueChange = (
		firstChange: { value: number | boolean | string | undefined; propertyName: keyof LoanCalculationInputs },
		secondChange: { value: number | boolean | string | undefined; propertyName: keyof LoanCalculationInputs },
		optionA: boolean,
		optionB: boolean
	) => {
		let ASSET_FORMATION = { ...loanCalculationValues.ASSET_FORMATION }
		if (optionA) {
			ASSET_FORMATION = {
				...ASSET_FORMATION,
				[firstChange.propertyName]: firstChange.value,
				[secondChange.propertyName]: secondChange.value,
			}
		}

		let PAYMENT_REDUCTION = { ...loanCalculationValues.PAYMENT_REDUCTION }
		if (optionB) {
			PAYMENT_REDUCTION = {
				...PAYMENT_REDUCTION,
				[firstChange.propertyName]: firstChange.value,
				[secondChange.propertyName]: secondChange.value,
			}
		}

		const changedFields = [firstChange.propertyName, secondChange.propertyName]
		if (changedFields.includes('fixedInterestPercentage')) {
			if (optionA) ASSET_FORMATION = { ...ASSET_FORMATION, useCustomInterestRate: true }
			if (optionB) PAYMENT_REDUCTION = { ...PAYMENT_REDUCTION, useCustomInterestRate: true }
		} else if (changedFields.includes('loanAmount') || changedFields.includes('propertyMarketValueAmount')) {
			if (optionA) ASSET_FORMATION = { ...ASSET_FORMATION, useCustomInterestRate: false }
			if (optionB) PAYMENT_REDUCTION = { ...PAYMENT_REDUCTION, useCustomInterestRate: false }
		}

		setLoanCalculationValues({ ASSET_FORMATION, PAYMENT_REDUCTION })
		setCalculatorDataToSessionStorage(ASSET_FORMATION, PAYMENT_REDUCTION)
	}

	const calculateData = useMemo(
		() =>
			debounce(async (newValues: LocalCalculationValues, options: GetMortgageSettingsResponse | null) => {
				const ASSET_FORMATION = (await calculateLoanData(
					newValues.ASSET_FORMATION,
					options,
					LoanTypeEnumCamelCase.AssetFormation,
					newValues.PAYMENT_REDUCTION.optionBLengthInYears * 12
				)) as LoanCalculationCalculatedData | null

				const PAYMENT_REDUCTION = (await calculateLoanData(
					newValues.PAYMENT_REDUCTION,
					options,
					LoanTypeEnumCamelCase.PaymentReduction,
					newValues.PAYMENT_REDUCTION.optionBLengthInYears * 12
				)) as LoanCalculationCalculatedData | null

				if (ASSET_FORMATION && PAYMENT_REDUCTION) {
					setCalculatedData({ ASSET_FORMATION, PAYMENT_REDUCTION })
					if (newValues.PAYMENT_REDUCTION.optionBAmount < PAYMENT_REDUCTION.paymentReductionMinAmount) {
						setLoanCalculationValues({
							ASSET_FORMATION: newValues.ASSET_FORMATION,
							PAYMENT_REDUCTION: {
								...newValues.PAYMENT_REDUCTION,
								optionBAmount: PAYMENT_REDUCTION.paymentReductionMinAmount,
							},
						})
					} else if (newValues.PAYMENT_REDUCTION.optionBAmount > PAYMENT_REDUCTION.paymentReductionMaxAmount) {
						setLoanCalculationValues({
							ASSET_FORMATION: newValues.ASSET_FORMATION,
							PAYMENT_REDUCTION: {
								...newValues.PAYMENT_REDUCTION,
								optionBAmount: PAYMENT_REDUCTION.paymentReductionMaxAmount,
							},
						})
					}
				}
			}, 500),
		[]
	)

	const activeCalculatedData = calculatedData
		? expandedPaymentPlan === LoanTypeEnum.ASSET_FORMATION
			? calculatedData.ASSET_FORMATION
			: calculatedData.PAYMENT_REDUCTION
		: undefined

	const validateLoanCalculationValues = (
		values: LocalCalculationValues,
		options: GetMortgageSettingsResponse | null
	) => {
		const { ASSET_FORMATION, PAYMENT_REDUCTION } = values

		const getMaxLtvRatio = (productType: LoanTypeEnumCamelCase) => {
			const rate = options?.interestRates.find((x) => x.productType === productType)
			return (rate?.ltvMaxThreshold ?? MaxLoanToPropertyValue * 100) / 100
		}

		const ltvMaxAF = getMaxLtvRatio(LoanTypeEnumCamelCase.AssetFormation)
		const ltvMaxPR = getMaxLtvRatio(LoanTypeEnumCamelCase.PaymentReduction)

		// No LTV-base floor on min — user can borrow down to minPrincipal regardless of LTV.
		const sharedMinPrincipal = options?.options.minPrincipalAmount ?? MinPrincipalAmount

		// Max loan = PMV * maxLtvRatio. No separate absolute maxPrincipalAmount cap — LTV is
		// the only constraint, so user can reach up to maxLtvRatio of the property value.
		const maxPrincipalAmount_ASSET_FORMATION = values.ASSET_FORMATION.propertyMarketValueAmount
			? values.ASSET_FORMATION.propertyMarketValueAmount * ltvMaxAF
			: (options?.options.maxPrincipalAmount ?? 195_000_000)

		const minPrincipalAmount_ASSET_FORMATION = sharedMinPrincipal

		const maxPrincipalAmount_PAYMENT_REDUCTION = values.PAYMENT_REDUCTION.propertyMarketValueAmount
			? values.PAYMENT_REDUCTION.propertyMarketValueAmount * ltvMaxPR
			: (options?.options.maxPrincipalAmount ?? 195_000_000)

		const minPrincipalAmount_PAYMENT_REDUCTION = sharedMinPrincipal

		if (options) {
			if (ASSET_FORMATION.loanAmount > maxPrincipalAmount_ASSET_FORMATION) {
				ASSET_FORMATION.loanAmount = maxPrincipalAmount_ASSET_FORMATION
			}
			if (ASSET_FORMATION.loanAmount < minPrincipalAmount_ASSET_FORMATION) {
				ASSET_FORMATION.loanAmount = minPrincipalAmount_ASSET_FORMATION
			}
			if (ASSET_FORMATION.numberOfPaymentsMonths > options.options.maxLoanLengthMonths) {
				ASSET_FORMATION.numberOfPaymentsMonths = options.options.maxLoanLengthMonths
			}
			if (ASSET_FORMATION.numberOfPaymentsMonths < options.options.minLoanLengthMonths) {
				ASSET_FORMATION.numberOfPaymentsMonths = options.options.minLoanLengthMonths
			}

			if (PAYMENT_REDUCTION.loanAmount > maxPrincipalAmount_PAYMENT_REDUCTION) {
				PAYMENT_REDUCTION.loanAmount = maxPrincipalAmount_PAYMENT_REDUCTION
			}
			if (PAYMENT_REDUCTION.loanAmount < minPrincipalAmount_PAYMENT_REDUCTION) {
				PAYMENT_REDUCTION.loanAmount = minPrincipalAmount_PAYMENT_REDUCTION
			}
			if (PAYMENT_REDUCTION.numberOfPaymentsMonths > options.options.maxLoanLengthMonths) {
				PAYMENT_REDUCTION.numberOfPaymentsMonths = options.options.maxLoanLengthMonths
			}
			if (PAYMENT_REDUCTION.numberOfPaymentsMonths < options.options.minLoanLengthMonths) {
				PAYMENT_REDUCTION.numberOfPaymentsMonths = options.options.minLoanLengthMonths
			}
			if (PAYMENT_REDUCTION.optionBLengthInYears > options.options.paymentReductionMaxLengthMonths / 12) {
				PAYMENT_REDUCTION.optionBLengthInYears = options.options.paymentReductionMaxLengthMonths / 12
			}
		}

		setLoanCalculationValues({ ASSET_FORMATION, PAYMENT_REDUCTION })
	}

	// Fetch the calculator settings once, on mount. Keeping this out of the
	// recalculation effect below is what prevents the endless refetch loop: when
	// the request fails, validateLoanCalculationValues used to re-set
	// loanCalculationValues, which re-triggered the effect and refetched forever.
	useEffect(() => {
		let cancelled = false
		let retryDelay = SETTINGS_INITIAL_RETRY_DELAY_MS
		let retryTimer: ReturnType<typeof setTimeout> | undefined

		const loadCalculatorOptions = async () => {
			const options = await getCalculatorOptions()
			if (cancelled) {
				return
			}

			if (options === null) {
				retryTimer = setTimeout(loadCalculatorOptions, retryDelay)
				retryDelay = Math.min(retryDelay * 2, SETTINGS_MAX_RETRY_DELAY_MS)
				return
			}

			setDefaultOptions(options)
			validateLoanCalculationValues(loanCalculationValues, options)
		}

		loadCalculatorOptions()

		return () => {
			cancelled = true
			if (retryTimer) {
				clearTimeout(retryTimer)
			}
		}
	}, [])

	// Recalculate whenever the inputs or the loaded settings change.
	useEffect(() => {
		if (defaultOptions !== null) {
			calculateData(loanCalculationValues, defaultOptions)
		}
	}, [loanCalculationValues, defaultOptions])

	const toggleExpandPaymentPlan = (planType: LoanTypeEnum) => {
		setExpandedPaymentPlan(expandedPaymentPlan === planType ? undefined : planType)
	}

	const changePublicShowSettings = (value: boolean) => {
		setPublicShowSettings(value)

		if (value === false) {
			setExpandedPaymentPlan(undefined)
		}
	}

	return {
		loanCalculationValues,
		calculatedData,
		activeCalculatedData,
		expandedPaymentPlan,
		publicShowSettings,
		setExpandedPaymentPlan,
		changePublicShowSettings,
		onInputValueChange,
		onMultipleInputValueChange,
		toggleExpandPaymentPlan,
		defaultOptions,
	}
}
